import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles, ShieldCheck, LogIn, LogOut } from "lucide-react";
import { SIDEBAR_NAV } from "../../lib/labs-config";
import { cn } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isSuperAdmin = (user?.roles || []).includes("super_admin");
  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "hidden lg:flex flex-col h-screen sticky top-0 transition-[width] duration-300 ease-out z-30",
        collapsed ? "w-[76px]" : "w-[260px]"
      )}
      style={{
        background: "var(--pl-sidebar)",
        backdropFilter: "blur(24px) saturate(140%)",
        WebkitBackdropFilter: "blur(24px) saturate(140%)",
        borderRight: "1px solid var(--pl-border)",
        boxShadow: "4px 0 32px -12px var(--pl-glow)",
      }}
    >
      <div className="flex items-center justify-between px-4 py-5 border-b border-[var(--pl-border)]">
        <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="sidebar-logo">
          <div className="relative h-9 w-9 rounded-xl pl-signature-gradient grid place-items-center shadow-[0_0_22px_rgba(39,230,209,0.45)]">
            <Sparkles className="h-4 w-4 text-[#061414]" strokeWidth={2.5} />
          </div>
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-display text-[15px] font-semibold text-[var(--pl-text)]">
                PremierLab<span className="pl-gradient-text">.ai</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
                Creative OS
              </div>
            </div>
          )}
        </Link>
        <button
          data-testid="sidebar-collapse-btn"
          onClick={onToggle}
          className="hidden lg:grid place-items-center h-7 w-7 rounded-md hover:bg-white/5 text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] transition"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto pl-noscroll px-3 py-4 space-y-5">
        {SIDEBAR_NAV.map((sec) => (
          <div key={sec.section}>
            {!collapsed && (
              <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">
                {sec.section}
              </div>
            )}
            <div className="space-y-1">
              {sec.items.map((item) => {
                const Active =
                  location.pathname === item.to ||
                  (item.to !== "/dashboard" && location.pathname.startsWith(item.to));
                const Icon = item.icon;
                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    data-testid={`sidebar-link-${item.id}`}
                    className={cn(
                      "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all",
                      Active
                        ? "bg-[var(--pl-aqua)]/10 text-[var(--pl-aqua)] pl-active-indicator"
                        : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)] hover:bg-white/5"
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-[18px] w-[18px] shrink-0 transition-colors",
                        Active ? "text-[var(--pl-aqua)]" : "group-hover:text-[var(--pl-aqua)]"
                      )}
                      strokeWidth={1.7}
                    />
                    {!collapsed && (
                      <>
                        <span className="font-medium tracking-tight">{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-md font-mono bg-[var(--pl-lime)]/15 text-[var(--pl-lime)] border border-[var(--pl-lime)]/30">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {isSuperAdmin && (
          <div>
            {!collapsed && (
              <div className="text-[10px] uppercase tracking-[0.22em] font-medium text-[var(--pl-aqua)] px-3 mb-2">
                Internal
              </div>
            )}
            <Link
              to="/admin"
              data-testid="sidebar-link-admin-portal"
              className={cn(
                "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all",
                location.pathname.startsWith("/admin")
                  ? "bg-[var(--pl-aqua)]/10 text-[var(--pl-aqua)] pl-active-indicator"
                  : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)] hover:bg-white/5"
              )}
            >
              <ShieldCheck
                className={cn("h-[18px] w-[18px] shrink-0 transition-colors",
                  location.pathname.startsWith("/admin")
                    ? "text-[var(--pl-aqua)]"
                    : "group-hover:text-[var(--pl-aqua)]")}
                strokeWidth={1.7}
              />
              {!collapsed && <span className="font-medium tracking-tight">Admin Console</span>}
            </Link>
          </div>
        )}
      </nav>

      {!collapsed && (
        <div className="m-3 p-4 rounded-2xl pl-glass relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[var(--pl-lime)]/30 blur-3xl pointer-events-none" />
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)] mb-1">
            AI Credits
          </div>
          <div className="font-display text-2xl font-semibold text-[var(--pl-text)]">
            38,750
          </div>
          <div className="text-xs text-[var(--pl-text-muted)] mb-3">
            of 50,000 monthly
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full pl-signature-gradient"
              style={{ width: "77%" }}
            />
          </div>
        </div>
      )}

      {/* Account footer — Sign in (logged out) OR account row + Sign out (logged in) */}
      <div className={cn("mx-3 mb-3 mt-1", collapsed && "mx-2")}>
        {!user ? (
          <Link
            to="/login"
            data-testid="sidebar-signin-btn"
            className={cn(
              "flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold pl-btn-aqua",
              collapsed && "p-2"
            )}
            title="Sign in"
          >
            <LogIn className="h-4 w-4" />
            {!collapsed && <span>Sign in</span>}
          </Link>
        ) : (
          <div className={cn("rounded-xl pl-glass", collapsed ? "p-1" : "p-2")}>
            {!collapsed && (
              <div className="flex items-center gap-2 px-1.5 py-1 mb-1.5">
                <span className="h-7 w-7 rounded-full pl-signature-gradient grid place-items-center text-[#061414] font-bold text-[10px] shrink-0">
                  {((user.name || user.email || "?").split(/\s|@/)[0]).slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-[var(--pl-text)] truncate">{user.name || user.email}</div>
                  <div className="text-[10px] text-[var(--pl-text-muted)] truncate">{user.email}</div>
                </div>
              </div>
            )}
            <button
              data-testid="sidebar-signout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className={cn(
                "w-full flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold transition",
                "text-rose-300 border border-rose-400/30 hover:bg-rose-500/10",
                collapsed && "py-2"
              )}
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
              {!collapsed && <span>Sign out</span>}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
